/**
 * Helpers for FilterResponsibilitiesModal apply payload ↔ chips / initial props /
 * applied-filter array / Responsibility v2 list API query params (ER-12617).
 *
 * Values are stable lowercase snake_case end-to-end (Filter Modal Standard);
 * labels are for display only:
 *   - statuses:  { draft, active, retired } (checkbox booleans)
 *   - archived:  do_not_show_archived (default) | show_archived
 *   - checklist: no_filter_checklist (default) | no_checklist | draft_only | has_active
 *   - roles:     no_filter_roles (default) | attached | no_role_attached
 *   - usage:     no_filter_usage (default) | in_use | not_used
 *   - coverage:  no_filter_coverage (default) | no_masters | no_coaches | no_professionals
 *   - timeline:  no_filter_timeline (default) | recently_activated | recently_retired | recently_created
 *
 * Chip suppression for the Archived default is keyed on the VALUE, never on the label.
 *
 * Legacy label strings persisted by the SearchModal-based filter ("Do not Show",
 * "No Masters", "No Checklist", …) are accepted when reading saved filters so state
 * survives the flag flip; new values are always emitted once the modal is used.
 */

export const STATUS_LABEL_BY_VALUE = {
  draft: "Draft",
  active: "Active",
  retired: "Retired",
};

export const ARCHIVED_LABEL_BY_VALUE = {
  do_not_show_archived: "Do Not Show",
  show_archived: "Show",
};

export const CHECKLIST_LABEL_BY_VALUE = {
  no_checklist: "No Checklist",
  draft_only: "Only Draft Checklist",
  has_active: "Has Active Checklist",
};

export const ROLES_LABEL_BY_VALUE = {
  attached: "Attached",
  no_role_attached: "No role attached",
};

export const USAGE_LABEL_BY_VALUE = {
  in_use: "In use",
  not_used: "Not used",
};

export const COVERAGE_LABEL_BY_VALUE = {
  no_masters: "No Architects",
  no_coaches: "No Coaches",
  no_professionals: "No Proficients",
};

export const TIMELINE_LABEL_BY_VALUE = {
  recently_activated: "Recently Activated",
  recently_retired: "Recently Retired",
  recently_created: "Recently Created",
};

export const SHOW_ARCHIVED_CHIP = "Show Archived";

export const DEFAULT_RESPONSIBILITY_FILTER_PAYLOAD = Object.freeze({
  statuses: Object.freeze({ draft: true, active: true, retired: false }),
  archived: "do_not_show_archived",
  checklist: "no_filter_checklist",
  roles: "no_filter_roles",
  usage: "no_filter_usage",
  coverage: "no_filter_coverage",
  timeline: "no_filter_timeline",
});

const RADIO_LABELS = {
  archived: ARCHIVED_LABEL_BY_VALUE,
  checklist: CHECKLIST_LABEL_BY_VALUE,
  roles: ROLES_LABEL_BY_VALUE,
  usage: USAGE_LABEL_BY_VALUE,
  coverage: COVERAGE_LABEL_BY_VALUE,
  timeline: TIMELINE_LABEL_BY_VALUE,
};

const RADIO_KEYS = Object.keys(RADIO_LABELS);

const LEGACY_ALIASES = {
  no_archived: ["archived", "do_not_show_archived"],
  "no masters": ["coverage", "no_masters"],
  "no professionals": ["coverage", "no_professionals"],
};

// SearchModal fallback "Attachments → No Roles"; the list API reads it as roles=no.
const LEGACY_NO_ROLES_KEYS = ["no roles", "no_roles"];

const RADIO_LOOKUP = (() => {
  const lookup = { ...LEGACY_ALIASES };
  RADIO_KEYS.forEach((radioKey) => {
    Object.entries(RADIO_LABELS[radioKey]).forEach(([value, label]) => {
      lookup[value] = [radioKey, value];
      lookup[label.toLowerCase()] = [radioKey, value];
    });
  });
  return lookup;
})();

export function cloneResponsibilityFilterPayload(payload = DEFAULT_RESPONSIBILITY_FILTER_PAYLOAD) {
  const next = {
    statuses: {
      draft: !!payload?.statuses?.draft,
      active: !!payload?.statuses?.active,
      retired: !!payload?.statuses?.retired,
    },
  };
  RADIO_KEYS.forEach((radioKey) => {
    next[radioKey] = payload?.[radioKey] || DEFAULT_RESPONSIBILITY_FILTER_PAYLOAD[radioKey];
  });
  return next;
}

/** Applied-filter chips (display labels); radio chips only for non-default values. */
export function buildResponsibilityFilterChips(payload = {}) {
  const chips = [];

  Object.entries(payload.statuses || {}).forEach(([key, checked]) => {
    if (checked && STATUS_LABEL_BY_VALUE[key]) {
      chips.push(STATUS_LABEL_BY_VALUE[key]);
    }
  });

  RADIO_KEYS.forEach((radioKey) => {
    const value = payload[radioKey];
    if (radioKey === "archived") {
      if (value === "show_archived") chips.push(SHOW_ARCHIVED_CHIP);
      return;
    }
    if (value && RADIO_LABELS[radioKey][value]) {
      chips.push(RADIO_LABELS[radioKey][value]);
    }
  });

  return chips;
}

/** Payload → applied-filter array of stable values; default radio values are omitted. */
export function payloadToAppliedFilters(payload = {}) {
  const filters = [];

  Object.entries(payload.statuses || {}).forEach(([key, checked]) => {
    if (checked && STATUS_LABEL_BY_VALUE[key]) {
      filters.push(key);
    }
  });

  RADIO_KEYS.forEach((radioKey) => {
    const value = payload[radioKey];
    if (value && value !== DEFAULT_RESPONSIBILITY_FILTER_PAYLOAD[radioKey]) {
      filters.push(value);
    }
  });

  return filters;
}

function toFilterString(filter) {
  const raw = filter !== null && typeof filter === "object" ? filter.value : filter;
  return String(raw ?? "").trim();
}

/**
 * Applied-filter array → modal payload. Accepts stable values, legacy label
 * strings and `{ labelType, value }` objects. Unknown entries ("No Filter",
 * "No Roles", …) are ignored.
 */
export function appliedFiltersToPayload(filters = []) {
  const entries = Array.isArray(filters) ? filters : [];
  const payload = cloneResponsibilityFilterPayload({
    statuses: { draft: false, active: false, retired: false },
  });

  entries.forEach((entry) => {
    const text = toFilterString(entry);
    if (!text) return;
    const key = text.toLowerCase();

    if (STATUS_LABEL_BY_VALUE[key]) {
      payload.statuses[key] = true;
      return;
    }

    const match = RADIO_LOOKUP[key];
    if (match) {
      const [radioKey, value] = match;
      payload[radioKey] = value;
    }
  });

  return payload;
}

/** Same filters expressed as stable values only (legacy labels normalised). */
export function normalizeResponsibilityFilters(filters = []) {
  return payloadToAppliedFilters(appliedFiltersToPayload(filters));
}

/**
 * Remove one summary chip (label or value) and return the updated payload
 * (immutable). Unknown chips leave the payload unchanged.
 */
export function removeResponsibilityFilterChip(payload, removedChip) {
  const next = cloneResponsibilityFilterPayload(payload);
  const removed = toFilterString(removedChip);
  if (!removed) return next;
  const key = removed.toLowerCase();

  if (STATUS_LABEL_BY_VALUE[key]) {
    next.statuses = { ...next.statuses, [key]: false };
    return next;
  }

  if (key === SHOW_ARCHIVED_CHIP.toLowerCase()) {
    next.archived = DEFAULT_RESPONSIBILITY_FILTER_PAYLOAD.archived;
    return next;
  }

  const match = RADIO_LOOKUP[key];
  if (match) {
    const [radioKey, value] = match;
    if (next[radioKey] === value) {
      next[radioKey] = DEFAULT_RESPONSIBILITY_FILTER_PAYLOAD[radioKey];
    }
  }
  return next;
}

/**
 * Applied-filter array (stable values or legacy labels) → Responsibility v2 list
 * API filters. The list always excludes archived responsibilities unless "Show"
 * is selected.
 */
export function convertResponsibilityFiltersToApiFormat(filters = []) {
  const payload = appliedFiltersToPayload(filters);
  const apiFilters = {};

  const status = Object.keys(STATUS_LABEL_BY_VALUE).filter((key) => payload.statuses[key]);
  if (status.length > 0) {
    apiFilters.status = status;
  }

  apiFilters.archived = payload.archived === "show_archived";

  if (payload.checklist !== DEFAULT_RESPONSIBILITY_FILTER_PAYLOAD.checklist) {
    apiFilters.checklist = payload.checklist;
  }
  if (payload.roles === "attached") apiFilters.attachments = "yes";
  if (payload.roles === "no_role_attached") apiFilters.attachments = "no";
  if (payload.usage === "in_use") apiFilters.in_use = true;
  if (payload.usage === "not_used") apiFilters.in_use = false;
  if (payload.coverage !== DEFAULT_RESPONSIBILITY_FILTER_PAYLOAD.coverage) {
    apiFilters[payload.coverage] = true;
  }
  if (payload.timeline !== DEFAULT_RESPONSIBILITY_FILTER_PAYLOAD.timeline) {
    apiFilters[payload.timeline] = true;
  }

  const entries = Array.isArray(filters) ? filters : [];
  if (entries.some((entry) => LEGACY_NO_ROLES_KEYS.includes(toFilterString(entry).toLowerCase()))) {
    apiFilters.roles = "no";
  }

  return apiFilters;
}
